import { apiFetch } from "../api.js";
import type {
  BacktestSignalRuleInput,
  BacktestSignalRuleResponse,
  CreateSignalRuleInput,
  SignalRule,
  SignalRuleConfig,
  SignalRulesListResponse,
  UpdateSignalRuleInput,
} from "./types.js";

/**
 * Signal-rules API module — household-scoped CRUD shipped in Phase 2
 * Session 2.4. Rules drive the daily 6:30pm-ET signal-scanner Lambda;
 * fired rules write into the existing `alerts` table.
 *
 * The rule body is a discriminated union (`threshold | cross | bb_band`).
 * The frontend constructs each shape in `/alerts` based on the user's
 * sub-form choice; the backend validates with the same Zod
 * `signalRuleConfigSchema`.
 */

export function listSignalRules(): Promise<SignalRulesListResponse> {
  return apiFetch<SignalRulesListResponse>("/v1/signal-rules");
}

export function getSignalRule(signalRuleId: string): Promise<SignalRule> {
  return apiFetch<SignalRule>(
    `/v1/signal-rules/${encodeURIComponent(signalRuleId)}`,
  );
}

export function createSignalRule(
  input: CreateSignalRuleInput,
): Promise<SignalRule> {
  return apiFetch<SignalRule>("/v1/signal-rules", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function updateSignalRule(
  signalRuleId: string,
  input: UpdateSignalRuleInput,
): Promise<SignalRule> {
  return apiFetch<SignalRule>(
    `/v1/signal-rules/${encodeURIComponent(signalRuleId)}`,
    {
      method: "PATCH",
      body: JSON.stringify(input),
    },
  );
}

export function deleteSignalRule(signalRuleId: string): Promise<void> {
  return apiFetch<void>(
    `/v1/signal-rules/${encodeURIComponent(signalRuleId)}`,
    { method: "DELETE" },
  );
}

/**
 * Backtest a hypothetical (or persisted) rule against the last N days
 * of EOD prices for a ticker. Used by the Backtest tab on /alerts and
 * the inline preview when authoring a rule.
 */
export function backtestSignalRule(
  input: BacktestSignalRuleInput,
): Promise<BacktestSignalRuleResponse> {
  return apiFetch<BacktestSignalRuleResponse>("/v1/signal-rules/backtest", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

/**
 * Render a rule config as a short human-readable string. Used in the
 * `/alerts` list view, the alert email template (server-side), and the
 * close-out flow when surfacing a rule context on a journal entry.
 */
export function describeRule(rule: SignalRuleConfig): string {
  if (rule.kind === "threshold") {
    return `${rule.indicator} ${rule.comparator} ${rule.value}`;
  }
  if (rule.kind === "cross") {
    const arrow = rule.direction === "up" ? "crosses above" : "crosses below";
    return `${rule.fast} ${arrow} ${rule.slow}`;
  }
  return `close ${rule.direction} bollinger ${rule.band} band (20/2)`;
}
